﻿import type { ApiClient } from './client';

export type HistoryTab = 'all' | 'fav' | 'circle' | 'video' | 'photo' | 'shoot' | 'audio' | 'text';
export type HistorySort = 'new' | 'old';
export interface HistoryRepeat {
  screen: string;
  app_id?: number;
  template_version?: number;
  inputs?: Record<string, unknown>;
  text?: string;
  voice_id?: number;
  project_id?: number;
  avatar_id?: number;
}
export interface HistoryAsset {
  index: number;
  mime: string;
  file_name?: string;
  download_url?: string;
  bytes?: number;
  width?: number;
  height?: number;
  duration_ms?: number;
}
export interface HistoryOrder {
  id: number;
  kind: string;
  screen: string;
  title?: string;
  /** Шаблон заказа (03-api §9.4: имя шаблона в шапке); null — экран без шаблона */
  app?: string | { id?: number; name?: string } | null;
  status: string;
  created_at: string;
  updated_at?: string;
  fav?: boolean;
  seen_at?: string | null;
  origin?: string;
  text?: string;
  voice_id?: number;
  price?: number;
  execution_state?: string;
  step_index?: number;
  steps_count?: number;
  steps?: Array<{ key: string; title?: string; state?: string; status?: string; progress?: number }>;
  progress?: number | { done: number; total: number; note?: string } | null;
  charge?: { price?: number; debited?: number; refunded?: number };
  error_code?: string | null;
  result_revision?: number;
  financial?: {
    state: string;
    amount_tokens?: string | number | null;
    charged_amount?: string | number | null;
    refunded_amount?: string | number | null;
    unit?: string;
  };
  delivery?: { state: string; allowed_formats?: string[]; error_code?: string | null };
  allowed_formats?: string[];
  display?: Record<string, unknown> | null;
  repeat?: HistoryRepeat | null;
  result?: {
    available?: boolean;
    unavailable_reason?: string;
    timeline?: unknown;
    source_lines?: Array<{ line_key: string; role_name?: string; text?: string; voice_name?: string; voice_id?: number }>;
    duration_ms?: number;
    mime?: string;
    assets?: HistoryAsset[];
    previews?: HistoryAsset[];
    download_url?: string | null;
    archive_url?: string | null;
    data?: unknown;
    text?: string;
    sections?: Array<{ kind: string; title?: string; name?: string; asset_indices: number[]; download_url?: string }>;
    slides?: unknown;
    avatar_id?: number;
    model_kind?: string;
    verification_state?: string;
  } | null;
}
export interface HistoryPage {
  items: HistoryOrder[];
  next_cursor: string | null;
  total?: number;
}
export interface HistoryStats {
  orders: number;
  works: number;
  fav: number;
  active?: number;
}
export interface HistoryQuery {
  tab: HistoryTab;
  q: string;
  sort: HistorySort;
}

/** Ни внешние ссылки, ни S3 URL из результата не используются как authenticated download. */
export function historyDownloadPath(id: number, url: string): string {
  const path = url.startsWith('/bot/v1/') ? url.slice('/bot/v1'.length) : url;
  const allowed = new RegExp(
    `^/app/orders/${id}/result/(?:audio|video|archive|files/\\d+(?:/poster)?)(?:\\?(?:revision=\\d+)(?:&section=[a-zA-Z0-9_%.-]+)?)?$`,
  );
  if (!Number.isSafeInteger(id) || id <= 0 || !allowed.test(path)) throw new Error('result_download_invalid');
  return path;
}
export const history = (client: ApiClient) => ({
  list: (query: HistoryQuery, cursor?: string | null, signal?: AbortSignal) =>
    client.request<HistoryPage>('/app/orders', {
      query: { ...query, cursor, limit: cursor ? 10 : 20 },
      signal,
    }),
  stats: (signal?: AbortSignal) => client.request<HistoryStats>('/app/history/stats', { signal }),
  active: (signal?: AbortSignal) => client.request<{ items: HistoryOrder[] }>('/app/orders/active', { signal }),
  get: (id: number, signal?: AbortSignal) => client.request<HistoryOrder>(`/app/orders/${id}`, { signal }),
  seen: (id: number, signal?: AbortSignal) => client.request<void>(`/app/orders/${id}/seen`, { method: 'POST', signal }),
  favorite: (id: number, fav: boolean, signal?: AbortSignal) =>
    client.request<{ fav: boolean }>(`/app/orders/${id}`, {
      method: 'PATCH',
      body: { fav },
      signal,
    }),
  hide: (id: number, signal?: AbortSignal) => client.request<void>(`/app/orders/${id}`, { method: 'DELETE', signal }),
  send: (order: HistoryOrder, format: string, key: string, signal?: AbortSignal) =>
    client.request<Partial<HistoryOrder> & { id: number }>(`/app/orders/${order.id}/send`, {
      method: 'POST',
      body: { format, result_revision: order.result_revision ?? 1 },
      idempotency: key,
      signal,
    }),
  download: (id: number, url: string, signal?: AbortSignal) => client.download(historyDownloadPath(id, url), signal),
});
